import { createContext, useContext, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { ActionButton } from "@react-spectrum/s2";
import BugIcon from "@react-spectrum/s2/icons/Bug";
import { useIsDevMode } from "@app/hooks/useRoles";

import {
  style,
  iconStyle,
  size,
} from "@react-spectrum/s2/style" with { type: "macro" };

export const DevToolbarContext = createContext(null);

export const DevToolbarProvider = ({ children }) => {
  // State (not a ref) so items re-render once the container mounts, e.g. after
  // dev mode is toggled on at runtime.
  const [container, setContainer] = useState(null);
  const [counter, setCounter] = useState(0);

  const register = () => {
    setCounter((prev) => prev + 1);
  };

  const unregister = () => {
    setCounter((prev) => Math.max(prev - 1, 0));
  };

  return (
    <DevToolbarContext.Provider
      value={{ container, setContainer, counter, register, unregister }}
    >
      {children}
      <DevToolbarContainer />
    </DevToolbarContext.Provider>
  );
};

export const DevToolbarContainer = () => {
  const context = useContext(DevToolbarContext);

  const { setContainer, counter } = context;

  const isDevMode = useIsDevMode();

  if (!isDevMode) {
    return null;
  }

  return (
    <div
      ref={setContainer}
      className={
        "__NotEmpty" +
        style({
          opacity: 0.5,
          gridArea: "steps",
          padding: 4,
          boxShadow: "elevated",
          backgroundColor: "transparent-overlay-100",
          borderRadius: "default",

          alignSelf: "end",
          marginStart: "auto",

          position: "sticky",
          top: size(72),
          right: 16,
          zIndex: 10,

          display: "flex",
          gap: 4,
          alignItems: "center",
        })
      }
    >
      {counter > 0 && (
        <BugIcon
          styles={
            style({ order: -1, marginStart: 4, marginEnd: -2, opacity: 0.25 }) +
            iconStyle({ size: "S" })
          }
        />
      )}
    </div>
  );
};

export const DevToolbarItem = (props) => {
  const context = useContext(DevToolbarContext);

  useEffect(() => {
    context?.register();

    return () => {
      context?.unregister();
    };
  }, [context]);

  const isDevMode = useIsDevMode();

  if (!context) {
    throw new Error("DevToolbar must be used within a DevToolbarProvider");
  }

  if (!isDevMode) {
    return null;
  }

  const { container } = context;

  if (!container) {
    return null;
  }

  return createPortal(
    <ActionButton size="S" isQuiet staticColor="auto" {...props} />,
    container,
  );
};
